import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { keyframes } from "@mui/system";
import { OrganizationUserContext } from "contexts/Organization";
import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "schemas/routes";

import {
  EMAIL_TARGET_FOR_FREE,
  EMAIL_TARGET_FOR_PRO,
} from "constants/influencer";
import {
  EmailStreakPerWeek,
  OrganizationGamificationTask,
} from "features/Influencer/HomePage/Gamification/schema";
import { trackEvent } from "utils/tracking";
import { useGamification } from "utils/useGamification";

import CardHeader from "../../components/CardHeader";
import WeeklyConsistencyChallengeLoading from "./WeeklyConssitencyChallengeLoading";
import { Icon } from "./shared/icons";
import {
  Callout,
  Confetti,
  HowItWorksModal,
  ModuleHeader,
  ModulePanel,
  Pill,
  ProgressBar,
  WeekCard,
  WeekStatus,
} from "./shared/primitives";
import { buttonSx, g } from "./shared/tokens";

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(181, 72, 46, 0.55); }
  50% { box-shadow: 0 0 0 8px rgba(181, 72, 46, 0); }
`;

const btnBase = {
  ...buttonSx.base,
  textTransform: "none" as const,
  letterSpacing: 0,
  fontWeight: 700,
  fontSize: 14,
  borderRadius: "999px",
};

const _CELEBRATION_WINDOW_MS = 24 * 60 * 60 * 1000;

interface Props {
  completed?: boolean;
}

const WeeklyConsistencyChallenge = ({ completed }: Props) => {
  const navigate = useNavigate();
  const { currentOrg, gamificationProfile, credits } = useContext(
    OrganizationUserContext,
  );
  const { profileLoading, refreshGamificationProfile, startEmailStreak } =
    useGamification();
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);

  const prevWeeklyGoalHitRef = useRef<boolean | null>(null);
  const fetchedStreaksRef = useRef(false);

  const target = currentOrg?.isProUser
    ? EMAIL_TARGET_FOR_PRO
    : EMAIL_TARGET_FOR_FREE;

  useEffect(() => {
    if (
      profileLoading ||
      fetchedStreaksRef.current ||
      !gamificationProfile?.emailStreakStartedAt ||
      gamificationProfile.emailStreaks
    ) {
      return;
    }
    fetchedStreaksRef.current = true;
    refreshGamificationProfile(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gamificationProfile, profileLoading]);

  useEffect(() => {
    if (profileLoading || !gamificationProfile) return;
    const streaks = gamificationProfile.emailStreaks ?? [];
    const thisWeek = streaks.find((s) => s.isCurrentWeek);
    const thisWeekCount = thisWeek?.emailCount ?? 0;
    const goalHit = Boolean(
      thisWeek && (thisWeek.completed || thisWeekCount >= target),
    );

    if (prevWeeklyGoalHitRef.current === null) {
      prevWeeklyGoalHitRef.current = goalHit;
      return;
    }
    if (goalHit && !prevWeeklyGoalHitRef.current) {
      let streak = 0;
      for (const week of streaks) {
        if (week.isCurrentWeek) {
          if (week.completed) streak += 1;
          break;
        }
        if (week.completed) streak += 1;
        else streak = 0;
      }
      trackEvent("Desktop Streak Completed", {
        "Current Streak": streak,
        "Weekly Target": target,
        "From Mobile App": false,
      });
    }
    prevWeeklyGoalHitRef.current = goalHit;
  }, [gamificationProfile, profileLoading, target]);

  if (profileLoading || !gamificationProfile) {
    return <WeeklyConsistencyChallengeLoading />;
  }

  const streakStarted = Boolean(gamificationProfile.emailStreakStartedAt);
  if (!streakStarted) {
    return (
      <Fragment>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
            flexWrap: "wrap",
            background:
              "linear-gradient(135deg, rgba(247, 238, 111, 0.35), #FFFFFF)",
            border: `1px solid ${g.yellowBorder}`,
            borderRadius: g.radiusLg,
            p: { xs: "16px", md: "16px 20px" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: g.radiusMd,
                background: g.primary,
                color: g.yellow,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <Icon.Trophy size={20} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: g.serif,
                  fontSize: 18,
                  fontWeight: 700,
                  color: g.primary,
                  lineHeight: 1.2,
                }}
              >
                Start a pitching streak
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: g.text2, mt: "2px" }}>
                Hit <b>{target} pitches a week for 4 weeks</b> and earn{" "}
                <b>$30 in Bento credit</b>.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexShrink: 0,
            }}
          >
            <Button
              sx={{ ...btnBase, ...buttonSx.primary }}
              disabled={profileLoading}
              onClick={() => {
                trackEvent("Gamification - Pitching Streak Started", {
                  "Weekly Target": target,
                });
                startEmailStreak();
              }}
            >
              Start now <Icon.ArrowRight size={14} />
            </Button>
            <Button
              sx={{ ...btnBase, ...buttonSx.ghost }}
              onClick={() => setHowItWorksOpen(true)}
            >
              How it works
            </Button>
          </Box>
        </Box>

        <HowItWorksModal
          open={howItWorksOpen}
          onClose={() => setHowItWorksOpen(false)}
          target={target}
        />
      </Fragment>
    );
  }

  const emailStreakCredit = credits?.find(
    (c) => c.transactionType === OrganizationGamificationTask.email_streak,
  );
  const creditGrantedAtMs = emailStreakCredit?.createdAt
    ? emailStreakCredit.createdAt * 1000
    : null;
  const inCelebrationWindow =
    Boolean(completed) &&
    creditGrantedAtMs !== null &&
    Date.now() - creditGrantedAtMs < _CELEBRATION_WINDOW_MS;
  const isCelebrating = Boolean(completed) && inCelebrationWindow;
  const isPostStreak = Boolean(completed) && !inCelebrationWindow;

  const emailStreaks = gamificationProfile.emailStreaks ?? [];
  const currentWeek = emailStreaks.find((streak) => streak.isCurrentWeek);
  const weekCount = currentWeek?.emailCount ?? 0;
  const completedWeeks = emailStreaks.filter((w) => w.completed).length;
  // Consecutive completed weeks ending at the current week. Resets on any
  // missed past week — this is what counts toward the 4-week reward.
  const currentStreak = (() => {
    let streak = 0;
    for (const week of emailStreaks) {
      if (week.isCurrentWeek) {
        if (week.completed) streak += 1;
        break;
      }
      if (week.completed) streak += 1;
      else streak = 0;
    }
    return streak;
  })();
  const weeklyGoalHit =
    !isCelebrating &&
    Boolean(currentWeek) &&
    (currentWeek?.completed || weekCount >= target);
  const remaining = Math.max(0, target - weekCount);
  const weeksToGo = Math.max(0, 4 - currentStreak);
  const progressPct = isCelebrating
    ? 100
    : Math.min(100, (weekCount / target) * 100);
  const showCta = !isCelebrating && !weeklyGoalHit && remaining > 0;
  const isPaid = Boolean(currentOrg?.isPaidUser);
  const isFresh = !completed && !weeklyGoalHit && completedWeeks === 0;
  const totalEmailsSent = emailStreaks.reduce(
    (sum, w) => sum + (w.emailCount ?? 0),
    0,
  );
  const isBrandNewUser =
    !completed &&
    !weeklyGoalHit &&
    completedWeeks === 0 &&
    totalEmailsSent === 0;
  const daysLeftInWeek = (() => {
    if (!currentWeek?.endAt) return null;
    // Append " UTC" so the week-end (Sunday midnight UTC) isn't parsed as local time.
    const end = new Date(`${currentWeek.endAt} UTC`);
    if (Number.isNaN(end.getTime())) return null;
    return Math.max(
      0,
      Math.ceil((end.getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
    );
  })();
  const isAtRisk =
    showCta &&
    !isBrandNewUser &&
    daysLeftInWeek !== null &&
    daysLeftInWeek > 0 &&
    daysLeftInWeek <= 3;
  const previousWeek = currentWeek
    ? emailStreaks.find((w) => w.index === currentWeek.index - 1)
    : undefined;
  const isBroken =
    showCta &&
    !isAtRisk &&
    completedWeeks > 0 &&
    Boolean(previousWeek && !previousWeek.completed);
  const userName = currentOrg?.name;

  const weekStatus = (week: EmailStreakPerWeek): WeekStatus => {
    const isFuture =
      !week.isCurrentWeek &&
      week.index > (currentWeek?.index ?? emailStreaks.length);
    if (isFuture) return "locked";
    if (week.completed) return "done";
    if (week.isCurrentWeek) return "now";
    return "missed";
  };

  const Wrapper = isPaid ? ModulePanel : Fragment;
  const wrapperProps = isPaid
    ? { sx: { background: g.cardBg, border: `1px solid ${g.border}` } }
    : {};

  const renderTitle = () => {
    let username = userName ? `${userName}, ` : "";
    if (isCelebrating) {
      return `${username}you earned $30 in Bento credit 🏆`;
    }
    if (isPostStreak) {
      return `${username}keep the momentum going`;
    }
    if (isAtRisk) {
      return `${username}your streak is at risk`;
    }
    if (isBroken) {
      return `${username}your streak broke — restart in one move`;
    }
    return `${username}keep your streak alive`;
  };

  return (
    <Wrapper {...wrapperProps}>
      {isPaid && (isCelebrating || weeklyGoalHit) && (
        <Confetti count={isCelebrating ? 36 : 22} />
      )}

      {isPaid && (
        <ModuleHeader
          titleSx={{ fontFamily: g.serif, fontWeight: 600 }}
          title={renderTitle()}
          sub={
            isPostStreak
              ? "Brand replies — not credits — are the real reward from here. Keep pitching to grow your reply rate."
              : "Turn today's actions into credits that you can use on your Bento subscription"
          }
          right={
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "flex-start", sm: "flex-end" },
                gap: "6px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {isAtRisk ? (
                  <Pill
                    variant="danger"
                    icon={<Icon.Bolt size={12} />}
                    sx={{ animation: `${pulse} 1.8s ease-in-out infinite` }}
                  >
                    {daysLeftInWeek} {daysLeftInWeek === 1 ? "day" : "days"}{" "}
                    left
                  </Pill>
                ) : isBroken ? (
                  <Pill variant="danger" icon={<Icon.Bolt size={12} />}>
                    Streak broken
                  </Pill>
                ) : isPostStreak ? (
                  <Pill variant="solid" icon={<Icon.Flame size={12} />}>
                    Streak active
                  </Pill>
                ) : currentStreak > 0 ? (
                  <Pill variant="solid" icon={<Icon.Flame size={12} />}>
                    {currentStreak}-week streak
                  </Pill>
                ) : (
                  <Pill variant="yellow" icon={<Icon.Sparkle size={12} />}>
                    Start Pitching to Earn
                  </Pill>
                )}

                <Button
                  size="small"
                  onClick={() => refreshGamificationProfile(true)}
                  disabled={profileLoading}
                  sx={{ minWidth: 0, p: "4px", color: g.text3 }}
                  aria-label="Refresh"
                >
                  <Box
                    component="i"
                    className="fa-regular fa-rotate-right"
                    sx={{ fontSize: 13 }}
                  />
                </Button>
              </Box>
              <Typography
                sx={{
                  fontSize: 11,
                  color: g.text3,
                  letterSpacing: "0.04em",
                  fontWeight: 600,
                  textTransform: "uppercase",
                }}
              >
                {isCelebrating
                  ? "REWARD UNLOCKED"
                  : isPostStreak
                    ? "$30 EARNED · ONE-TIME"
                    : `${weeksToGo} ${weeksToGo === 1 ? "week" : "weeks"} to $30`}
              </Typography>
            </Box>
          }
        />
      )}

      {/* Inner card */}
      <Box
        sx={{
          background: g.cardInnerBg,
          borderRadius: g.radiusLg,
          p: { xs: "16px", md: "22px 22px 18px" },
        }}
      >
        {/* Inner header */}
        <CardHeader
          icon={<Icon.Trophy size={20} />}
          iconSx={{ background: g.yellowBg, color: g.primary }}
          title={
            isPostStreak ? "Streak tracker" : "Weekly Consistency Challenge"
          }
          subtitle={
            isPostStreak
              ? "Keep your weekly pitch volume up — that's what lands brand deals."
              : isFresh
                ? `Send ${target} pitches every week for 4 consecutive weeks to unlock $30 in site credit.`
                : undefined
          }
          chip={
            isCelebrating ? (
              <Pill
                variant="yellow"
                icon={<Icon.Dollar size={14} />}
                sx={{ background: "#eed13b" }}
              >
                +$30 credited
              </Pill>
            ) : !isPostStreak ? (
              <Pill
                variant="yellow"
                icon={<Icon.Dollar size={14} />}
                sx={{ background: "#eed13b" }}
              >
                One-time $30 reward
              </Pill>
            ) : undefined
          }
        />

        {/* Callout */}
        {isCelebrating ? (
          <Callout tone="green" icon={<Icon.Sparkle size={16} />}>
            <b>4-week streak complete.</b> You showed up every week — exactly
            what brand deals require.
          </Callout>
        ) : weeklyGoalHit ? (
          <Callout tone="green" icon={<Icon.Sparkle size={16} />}>
            <b>Weekly goal hit! 🎉</b> Your streak rolls into next week
            automatically at Sunday midnight UTC. Anything extra this week still
            counts toward replies - pitch volume = brand deals.
          </Callout>
        ) : isAtRisk ? (
          <Callout tone="danger" icon={<Icon.Bolt size={16} />}>
            <b>
              {daysLeftInWeek} {daysLeftInWeek === 1 ? "day" : "days"} left,{" "}
              {remaining} {remaining === 1 ? "pitch" : "pitches"} to go.
            </b>{" "}
            {currentStreak > 0
              ? `A focused block of pitches today keeps your ${currentStreak}-week streak alive.`
              : isPostStreak
                ? "A focused block of pitches today keeps your streak alive."
                : "A focused block of pitches today keeps you on track for the $30 credit."}
          </Callout>
        ) : isBroken ? (
          <Callout tone="danger" icon={<Icon.Flame size={16} />}>
            <b>You missed last week.</b>{" "}
            {isPostStreak
              ? "Restart your streak today — you've got the muscle memory now."
              : "Restart your 4-week streak today — you've got the muscle memory now."}
          </Callout>
        ) : isPostStreak ? (
          <Callout tone="green" icon={<Icon.Sparkle size={16} />}>
            <b>You&apos;ve already earned your $30.</b> Brand replies — not
            credits — are the real reward from here. The longer your streak, the
            more deals close.
          </Callout>
        ) : isPaid && isBrandNewUser ? (
          <Callout tone="yellow" icon={<Icon.Sparkle size={16} />}>
            <b>Pitch and earn.</b> The streak runs on the calendar week (resets
            at Sunday midnight UTC), so any pitches you&apos;ve created this
            week already count. Hit {target} pitches a week for 4 weeks and
            we&apos;ll auto-credit $30 to your account.
          </Callout>
        ) : (
          <Callout tone="yellow" icon={<Icon.TrendUp size={16} />}>
            <b>The reality check:</b> Most creators give up after 1–2 weeks, but
            pitching is a numbers game. Brand deals are landed most often by
            creators who show up week after week.
          </Callout>
        )}

        {/* Week grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
            },
            gap: "10px",
            mt: "18px",
          }}
        >
          {emailStreaks.map((week) => (
            <WeekCard
              key={week.index}
              label={
                isMobileScreen ? `Week ${week.index}` : `Week ${week.startAt}`
              }
              value={week.emailCount}
              total={target}
              status={weekStatus(week)}
            />
          ))}
        </Box>

        {/* This week's progress */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: "22px",
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: g.text }}>
            This Week&apos;s Progress
          </Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: g.primary }}>
            {weekCount >= target
              ? "Target achieved! 🎉"
              : `${weekCount}/${target} pitches`}
          </Typography>
        </Box>
        <Box sx={{ mt: "8px" }}>
          <ProgressBar value={progressPct} />
        </Box>

        {/* Next steps */}
        {isCelebrating ? (
          <Typography sx={{ mt: "14px", fontSize: 13, color: g.text2 }}>
            <b>Next steps:</b> $30 site credit is already on your account.
          </Typography>
        ) : (
          showCta && (
            <Typography sx={{ mt: "14px", fontSize: 13, color: g.text2 }}>
              <b>Next steps:</b> Send {remaining} more{" "}
              {remaining === 1 ? "pitch" : "pitches"}{" "}
              {isPaid && isFresh
                ? "before Sunday midnight UTC to lock in week 1."
                : "before Sunday midnight UTC  to keep your streak alive"}
            </Typography>
          )
        )}

        {/* Footer action */}
        {isCelebrating ? (
          <Box
            sx={{
              mt: "14px",
              display: "flex",
              alignItems: "center",
              gap: "14px",
              p: "14px 16px",
              background: g.primaryTint,
              border: `1px solid ${g.primaryTintBorder}`,
              borderRadius: g.radiusMd,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "999px",
                background: g.primary,
                color: g.yellow,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <Icon.Check size={16} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{ fontSize: 14, fontWeight: 700, color: g.primary }}
              >
                $30 site credit applied to your account
              </Typography>
              <Typography sx={{ fontSize: 12, color: g.text2 }}>
                It&apos;ll show up on your next invoice — no claim needed. Keep
                pitching to maintain your streak.
              </Typography>
            </Box>
          </Box>
        ) : weeklyGoalHit ? (
          <Box
            sx={{
              mt: "14px",
              p: "14px 16px",
              background: g.primaryTint,
              border: `1px solid ${g.primaryTintBorder}`,
              borderRadius: g.radiusMd,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "14px",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "999px",
                  background: g.primary,
                  color: g.yellow,
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                <Icon.Check size={16} />
              </Box>
              <Box>
                <Typography
                  sx={{ fontSize: 14, fontWeight: 700, color: g.primary }}
                >
                  Week locked in — next week starts at Sunday midnight UTC
                </Typography>
                <Typography sx={{ fontSize: 12, color: g.text2 }}>
                  Extra pitches still help your reply rate.
                </Typography>
              </Box>
            </Box>
            <Button
              sx={{ ...btnBase, ...buttonSx.ghost }}
              onClick={() => {
                trackEvent("Gamification - Send More Emails Button Clicked");
                navigate(`/${routes.brands}`);
              }}
            >
              <Icon.Send size={14} /> Keep pitching
            </Button>
          </Box>
        ) : (
          showCta &&
          (isAtRisk ? (
            <Box sx={{ mt: "14px" }}>
              <Button
                sx={{ ...btnBase, ...buttonSx.danger }}
                onClick={() => {
                  trackEvent("Gamification - Send More Emails Button Clicked");
                  navigate(`/${routes.brands}`);
                }}
              >
                <Icon.Bolt size={14} /> Send pitches now · {remaining} to go
              </Button>
            </Box>
          ) : isBroken ? (
            <Box
              sx={{
                display: "flex",
                gap: "10px",
                mt: "14px",
                flexWrap: "wrap",
              }}
            >
              <Button
                sx={{ ...btnBase, ...buttonSx.primary }}
                onClick={() => {
                  trackEvent("Gamification - Send More Emails Button Clicked");
                  navigate(`/${routes.brands}`);
                }}
              >
                <Icon.Send size={14} /> Restart streak — send today
              </Button>
              <Button
                sx={{ ...btnBase, ...buttonSx.ghost }}
                onClick={() => setHowItWorksOpen(true)}
              >
                See how the challenge works
              </Button>
            </Box>
          ) : weekCount >= 1 ? (
            <Box sx={{ mt: "14px" }}>
              <Button
                sx={{
                  ...btnBase,
                  ...(isPostStreak ? buttonSx.primary : buttonSx.ghost),
                }}
                onClick={() => {
                  trackEvent("Gamification - Send More Emails Button Clicked");
                  navigate(`/${routes.brands}`);
                }}
              >
                {isPostStreak ? (
                  <>
                    <Icon.Send size={14} /> Keep the streak alive
                  </>
                ) : (
                  <>
                    Send more emails <Icon.Send size={14} />
                  </>
                )}
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                gap: "10px",
                mt: "14px",
                flexWrap: "wrap",
              }}
            >
              <Button
                sx={{ ...btnBase, ...buttonSx.primary }}
                onClick={() => {
                  trackEvent("Gamification - Send More Emails Button Clicked");
                  navigate(`/${routes.brands}`);
                }}
              >
                <Icon.Send size={14} /> Send your next pitch
              </Button>
              <Button
                sx={{ ...btnBase, ...buttonSx.ghost }}
                onClick={() => setHowItWorksOpen(true)}
              >
                See how the challenge works
              </Button>
            </Box>
          ))
        )}
      </Box>

      <HowItWorksModal
        open={howItWorksOpen}
        onClose={() => setHowItWorksOpen(false)}
        target={target}
      />
    </Wrapper>
  );
};

export default WeeklyConsistencyChallenge;
